import React, { useEffect, useState } from 'react';
import { tv } from 'tailwind-variants';

export type AlertVariant = 'success' | 'error' | 'danger' | 'warning' | 'info';

export interface AlertProps {
  show?: boolean;
  variant?: AlertVariant;
  onClose?: () => void;
  dismissible?: boolean;
  closeVariant?: 'white';
  className?: string;
  id?: string;
  children: React.ReactNode;
}

type TvVariant = 'success' | 'error' | 'warning' | 'info';

const resolveVariant = (variant?: string): TvVariant => {
  if (variant === 'danger') return 'error';
  if (variant === 'success' || variant === 'warning' || variant === 'info') return variant;
  if (variant === 'error') return 'error';
  return 'info';
};

const alertVariants = tv({
  base: 'flex items-start gap-[18px] rounded-lg border p-4 text-sm leading-5 font-normal [&+[role=alert]]:mt-3 transition-opacity duration-300 z-20 relative',
  variants: {
    variant: {
      success: 'bg-success-lightest border-success-medium text-success-darkest',
      error: 'bg-error-lightest border-error-dark text-error-dark',
      warning: 'bg-warning-lightest border-warning-medium text-warning-darkest',
      info: 'bg-info-lightest border-info-medium text-info-darkest',
    },
    visible: {
      true: 'opacity-100',
      false: 'opacity-0',
    },
  },
  defaultVariants: { variant: 'info', visible: true },
});

const Alert = ({
  show = true,
  variant,
  onClose,
  dismissible,
  closeVariant,
  className,
  id,
  children,
}: AlertProps) => {
  const [mounted, setMounted] = useState(show);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setMounted(true);
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frame);
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(timer);
    }
  }, [show]);

  if (!mounted) return null;

  const resolved = resolveVariant(variant as string | undefined);

  return (
    <div
      role="alert"
      id={id}
      className={alertVariants({
        variant: resolved,
        visible,
        class: className,
      })}
    >
      <div className="flex-1">{children}</div>
      {dismissible && onClose && (
        <button
          type="button"
          aria-label="Fechar"
          onClick={onClose}
          className="shrink-0 size-[14px] opacity-70 hover:opacity-100 transition-opacity"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0.342 0.342a1.167 1.167 0 0 1 1.65 0L7 5.35 12.008.342a1.167 1.167 0 0 1 1.65 1.65L8.65 7l5.008 5.008a1.167 1.167 0 0 1-1.65 1.65L7 8.65l-5.008 5.008a1.167 1.167 0 0 1-1.65-1.65L5.35 7 .342 1.992a1.167 1.167 0 0 1 0-1.65Z"
              fill="currentColor"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default Alert;
